import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { ToastContainer } from "react-toastify";
import axios from "axios";
import { Button, Card, PageHeader, Plus, Tabs } from "@opendate-app/setlist";

import { toastError } from "../../../shared/toastHelper";
import MarketingVenueSelector from "../shared/MarketingVenueSelector";
import VenueSelectionModal from "../shared/VenueSelectionModal";
import SearchBar from "../shared/SearchBar";
import { readListParams, writeListParams } from "../shared/listUrlState";
import { FAMILIES } from "./familiesConfig";
import FamilySidebar from "./FamilySidebar";
import SegmentFamily from "./SegmentFamily";
import EventAudiencesList from "./EventAudiencesList";
import CustomSegmentsList from "./CustomSegmentsList";
import { countOrUndefined } from "../../shared/components/CountBadge";

const sectionLabelStyle = { fontSize: "14px", lineHeight: "20px", fontWeight: 600, color: "var(--sl-text-tertiary)" };
const emptyTextStyle = { padding: "32px", textAlign: "center", fontSize: "14px", lineHeight: "20px", color: "var(--sl-text-secondary)" };
const bodyStyle = { padding: "12px 24px 24px" };

const VIEW_SLUGS = { smart: "smart-segments", events: "event-audiences", custom: "custom-segments" };
const SLUG_TO_VIEW = { "smart-segments": "smart", "event-audiences": "events", "custom-segments": "custom" };

const LIST_DEFAULTS = { tab: "smart", cat: "all", q: "" };

const initialView = (tab) => {
  const legacy = SLUG_TO_VIEW[window.location.hash.replace(/^#/, "")];
  const hasExplicitTab = new URLSearchParams(window.location.search).has("tab");
  if (tab === LIST_DEFAULTS.tab && legacy && !hasExplicitTab) return legacy;
  return VIEW_SLUGS[tab] ? tab : legacy || "smart";
};

const SegmentsDashboard = ({
  event_audiences_data_source,
  custom_segments_data_source,
  family_segments,
  event_audiences_count,
  custom_segments_count,
  giving_enabled,
  classes_enabled,
  pass_holders_enabled,
  team,
  teamMembership,
  csrfToken,
  enforceMarketingVenueAccessControl,
  venueOwnerships,
}) => {
  const listPath = `/teams/${team.id}/segments`;
  const [initial] = useState(() => readListParams(LIST_DEFAULTS));
  const [view, setView] = useState(() => initialView(initial.tab));
  const [requestedCat, setSelectedCat] = useState(initial.cat);
  const [searchTerm, setSearchTerm] = useState(initial.q);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [showVenueModal, setShowVenueModal] = useState(false);
  const [eventResult, setEventResult] = useState({ term: null, count: null });
  const [customResult, setCustomResult] = useState({ term: null, count: null });
  const families = FAMILIES.filter((f) => {
    if (f.key === "giving") return giving_enabled;
    if (f.key === "classes") return classes_enabled;
    if (f.key === "pass_holders") return pass_holders_enabled;
    return true;
  });

  const selectedCat = requestedCat === "all" || families.some((f) => f.key === requestedCat) ? requestedCat : "all";

  useEffect(() => {
    writeListParams(listPath, { tab: view, cat: selectedCat, q: searchTerm }, LIST_DEFAULTS);
    if (window.location.hash) {
      window.history.replaceState(window.history.state, "", window.location.pathname + window.location.search);
    }
  }, [view, selectedCat, searchTerm]);

  const segments = family_segments || [];
  const q = searchTerm.trim().toLowerCase();
  const filteredSegments = q
    ? segments.filter((s) =>
        (s.name || "").toLowerCase().includes(q) || (s.definition || "").toLowerCase().includes(q))
    : segments;

  const counts = { all: segments.length };
  families.forEach((f) => {
    counts[f.key] = segments.filter((s) => s.family_key === f.key).length;
  });

  const views = [
    { value: "smart", label: "Smart segments", count: countOrUndefined(segments.length) },
    { value: "events", label: "Event audiences", count: countOrUndefined(event_audiences_count) },
    { value: "custom", label: "Custom segments", count: countOrUndefined(custom_segments_count) },
  ];

  const searching = q.length > 0;

  const handleNewSegment = async () => {
    if (!enforceMarketingVenueAccessControl) {
      window.location.href = `/teams/${team.id}/segments/new`;
      return;
    }
    try {
      axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
      const { data } = await axios.get(`/teams/${team.id}/team_memberships/${teamMembership.id}`);
      if (data.selected_marketing_venue_ownership?.id) {
        window.location.href = `/teams/${team.id}/segments/new?venue_ownership_id=${data.selected_marketing_venue_ownership.id}`;
      } else {
        setShowVenueModal(true);
      }
    } catch (error) {
      toastError("Could not fetch venue information, please try again");
    }
  };

  const handleVenueModalConfirm = (venueOwnershipId) => {
    window.location.href = `/teams/${team.id}/segments/new?venue_ownership_id=${venueOwnershipId}`;
    setShowVenueModal(false);
  };

  const renderSmartMain = () => {
    if (selectedCat === "all") {
      const populated = families.filter((f) => filteredSegments.some((s) => s.family_key === f.key));
      if (populated.length === 0) {
        return (
          <div style={{ ...emptyTextStyle, flex: "999 1 480px" }}>
            {q ? `No segments match “${searchTerm}”.` : "No segments yet."}
          </div>
        );
      }
      return (
        <div style={{ flex: "999 1 480px", minWidth: 0, display: "flex", flexDirection: "column", gap: "28px" }}>
          {families.map((family) => (
            <SegmentFamily key={family.key} family={family} segments={filteredSegments} teamId={team.id} showEmptyState={false} />
          ))}
        </div>
      );
    }

    const family = families.find((f) => f.key === selectedCat);
    if (!family) return null;
    return (
      <div style={{ flex: "999 1 480px", minWidth: 0 }}>
        <SegmentFamily family={family} segments={filteredSegments} teamId={team.id} showEmptyState />
      </div>
    );
  };

  const renderSearchResults = () => {
    const smartPopulated = families.some((f) => filteredSegments.some((s) => s.family_key === f.key));
    const eventResultCount = eventResult.term === q ? eventResult.count : null;
    const customResultCount = customResult.term === q ? customResult.count : null;
    const noResults = !smartPopulated && eventResultCount === 0 && customResultCount === 0;
    return (
      <div style={{ ...bodyStyle, display: "flex", flexDirection: "column", gap: "28px" }}>
        {smartPopulated && (
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <div style={sectionLabelStyle}>Smart segments</div>
            {families.map((family) => (
              <SegmentFamily key={family.key} family={family} segments={filteredSegments} teamId={team.id} showEmptyState={false} />
            ))}
          </div>
        )}
        {eventResultCount !== 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <div style={sectionLabelStyle}>Event audiences</div>
            <EventAudiencesList
              key={`search-events-${lastUpdated.getTime()}-${q}`}
              dataSource={event_audiences_data_source}
              teamId={team.id}
              searchTerm={q}
              onResultCount={(n) => setEventResult({ term: q, count: n })}
            />
          </div>
        )}
        {customResultCount !== 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <div style={sectionLabelStyle}>Custom segments</div>
            <CustomSegmentsList
              key={`search-custom-${lastUpdated.getTime()}-${q}`}
              dataSource={custom_segments_data_source}
              teamId={team.id}
              csrfToken={csrfToken}
              searchTerm={q}
              onResultCount={(n) => setCustomResult({ term: q, count: n })}
            />
          </div>
        )}
        {noResults && (
          <div style={emptyTextStyle}>
            No segments match “{searchTerm.trim()}”.
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <ToastContainer icon={false} />
      <PageHeader
        title="Segments"
        supportingText="Every automatic segment, grouped by how fans engage with your venue."
        actions={(
          <>
            {enforceMarketingVenueAccessControl && (
              <MarketingVenueSelector teamId={team.id} teamMembership={teamMembership} csrfToken={csrfToken} onChange={() => setLastUpdated(new Date())} />
            )}
            <Button variant="primary" icon={Plus} onClick={handleNewSegment}>New segment</Button>
          </>
        )}
      />
      <Card padding="none" style={{ marginTop: 24 }}>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: "12px", padding: "12px 24px", borderBottom: "1px solid var(--sl-border-secondary)" }}>
          {searching ? (
            <span style={{ ...sectionLabelStyle, color: "var(--sl-text-secondary)" }}>Results for “{searchTerm.trim()}”</span>
          ) : (
            <div style={{ flex: "0 1 auto", minWidth: 0 }}>
              <Tabs
                variant="button-border"
                collapseBelow={0}
                overflow
                aria-label="Segment views"
                items={views}
                value={view}
                onChange={setView}
              />
            </div>
          )}
          <SearchBar key="segments-search" defaultValue={initial.q} placeholder="Search segments" onDebouncedChange={setSearchTerm} />
        </div>

        {searching ? renderSearchResults() : (
          <>
            {view === "smart" && (
              <div style={{ ...bodyStyle, display: "flex", flexWrap: "wrap", gap: "24px", alignItems: "flex-start" }}>
                <FamilySidebar families={families} selected={selectedCat} counts={counts} onSelect={setSelectedCat} />
                {renderSmartMain()}
              </div>
            )}

            {view === "events" && (
              <div style={bodyStyle}>
                <EventAudiencesList key={`events-${lastUpdated.getTime()}`} dataSource={event_audiences_data_source} teamId={team.id} />
              </div>
            )}

            {view === "custom" && (
              <div style={bodyStyle}>
                <CustomSegmentsList key={`custom-${lastUpdated.getTime()}`} dataSource={custom_segments_data_source} teamId={team.id} csrfToken={csrfToken} />
              </div>
            )}
          </>
        )}
      </Card>

      <VenueSelectionModal
        isOpen={showVenueModal}
        onClose={() => setShowVenueModal(false)}
        onConfirm={handleVenueModalConfirm}
        venueOwnerships={venueOwnerships}
        title="Select a Venue"
        subtitle="Choose a venue to associate with this segment"
      />
    </div>
  );
};

SegmentsDashboard.propTypes = {
  event_audiences_data_source: PropTypes.string.isRequired,
  custom_segments_data_source: PropTypes.string.isRequired,
  family_segments: PropTypes.arrayOf(PropTypes.object),
  event_audiences_count: PropTypes.number,
  custom_segments_count: PropTypes.number,
  giving_enabled: PropTypes.bool,
  classes_enabled: PropTypes.bool,
  pass_holders_enabled: PropTypes.bool,
  team: PropTypes.object.isRequired,
  teamMembership: PropTypes.object.isRequired,
  csrfToken: PropTypes.string.isRequired,
  enforceMarketingVenueAccessControl: PropTypes.bool,
  venueOwnerships: PropTypes.array,
};

export default SegmentsDashboard;
